import {
  adminSensorVars,
  defaultSensorVars,
  deleteSensors,
  ecopetrolSensorVars,
  kocSensorVars,
  restoreSensors
} from "@/models/equipment/sensors/predicates";
import type { Sensors } from "gql/api";
import {
  useDeleteSensorsMutation,
  useInsertSensorMutation,
  useRestoreSensorsMutation,
  useSensorsQuery,
  useUpdateSensorMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { sensorTableColumns } from "./sensorTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useSensors(isAdmin: boolean): UseEquipment<Sensors> {
  const baseEquipment = useEquipment<Sensors>(isAdmin);
  const title = "Sensors";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocSensorVars;
    if (user.is_ecopetrol_customer) return ecopetrolSensorVars;
    if (user.is_admin) return adminSensorVars;
    return defaultSensorVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useSensorsQuery({
    variables: getVariables()
  });

  const allRows = computed(() => data.value?.sensors as Sensors[] | undefined);

  const hashOrder: (keyof Sensors)[] = ["part_number", "model"];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertSensor } = useInsertSensorMutation();
  const { executeMutation: executeUpdateSensor } = useUpdateSensorMutation();
  const { executeMutation: executeDeleteSensor } = useDeleteSensorsMutation();
  const { executeMutation: executeRestoreSensor } = useRestoreSensorsMutation();

  const insertSensor = (
    sensor: Omit<Sensors, "housings" | "sizing_import" | "user">
  ) => executeInsertSensor({ object: sensor });

  const updateSensor = (id: string, sensor: Sensors) =>
    executeUpdateSensor({ id, object: sensor });

  const deleteSensor = (ids: string[]) =>
    executeDeleteSensor({ where: deleteSensors(ids) });

  const restoreSensor = (ids: string[]) =>
    executeRestoreSensor({ where: restoreSensors(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "sensors",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: null,
    length_inches: null,
    weight_pounds: null,
    neck_outer_diameter_inches: 1,
    model: null,
    channels: null,
    max_intake_temperature_fahrenheit: null,
    max_intake_pressure_psi: null,
    housing_material: null
  }));

  return {
    searchPlaceholder: "Search Sensors...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "375"`,
    columns: defineVisibleColumns(sensorTableColumns),
    handleUpsert: defineUpsert(insertSensor, updateSensor),
    handleDelete: defineDelete(deleteSensor),
    handleRestore: defineRestore(restoreSensor),
    totalCount: computed(() => data.value?.sensors_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: sensorTableColumns,
      globalSearchFields: ["channels", "model", "manufacturer"],
      allRows,
      title
    })
  };
}
